import { useEffect } from 'react';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useTableParams } from 'business/providers/table-params';
import { useRingComplianceReportFilters } from 'business/quality-control/pages/ring-compliance-report/providers/filters';
import { RingComplianceReportFilterData } from 'business/quality-control/pages/ring-compliance-report/types';
import { useRingValidationV1ControllerGetList } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';
import { SORT_ORDER } from 'technical/graphql/sort-order';

export const useGetListData = (
  setFilterData: FilterDataSetter<RingComplianceReportFilterData>,
) => {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');

  const filters = useRingComplianceReportFilters();

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const tableParams = useTableParams();

  const sortField = tableParams.tableParams.sortField;
  const sortOrder = tableParams.tableParams.sortOrder
    ? SORT_ORDER[tableParams.tableParams.sortOrder]
    : null;

  const { data, isLoading, error } = useRingValidationV1ControllerGetList({
    pathParams: {
      constructionSiteId: currentConstructionSiteId,
    },
    queryParams: {
      ringNumberStart: filters.ringNumberRange?.[0] ?? null,
      ringNumberEnd: filters.ringNumberRange?.[1] ?? null,
      status: filters.status,
      complianceDateStart:
        filters.complianceDateRange?.[0]?.startOf('day').toISOString() ?? null,
      complianceDateEnd:
        filters.complianceDateRange?.[1]?.endOf('day').toISOString() ?? null,
      approvalDateStart:
        filters.approvalDateRange?.[0]?.startOf('day').toISOString() ?? null,
      approvalDateEnd:
        filters.approvalDateRange?.[1]?.endOf('day').toISOString() ?? null,
      paginationPage: tableParams.tableParams.current - 1, // we want to start at index 0,
      paginationSize: tableParams.tableParams.pageSize,
      paginationSortField: sortField?.toString(),
      paginationSortDirection: sortOrder,
    },
    headers,
  });

  useEffect(() => {
    setFilterData({
      selectedRings: data?.total ?? 0,
      waitingRings: data?.waiting ?? 0,
      compliantRings: data?.compliant ?? 0,
      approvedRings: data?.approved ?? 0,
      refusedRings: data?.refused ?? 0,
    });
  }, [data, filters, setFilterData]);

  return {
    data: data?.entries ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
  };
};
